import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import useLogout from "../../Hooks/Logout";
import { getDashboardData } from "../../api/question";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  PieChartExample,
  PreviewAltCard,
} from "../../components/Component";
import { Card, Col, Row } from "reactstrap";
import "./dashboard.scss";
import Head from "../../layout/head/Head";
import SaleRevenue from "../../components/partials/sales/sale-revenue/SaleRevenue";
import ActiveSubscription from "../../components/partials/sales/active-subscription/ActiveSubscription";
import AvgSubscription from "../../components/partials/sales/avg-subscription/AvgSubscription";
import SalesOverview from "../../components/partials/sales/sales-overview/SalesOverview";
import NewsUsers from "../../components/partials/sales/new-users/User";
import Content from "../../layout/content/Content";
import { Link } from "react-router-dom";
import MyLoader from "../../MyComponents/Form/MyLoader";
import { formatDateTimeIST } from "../../utils/Utils";

/** Icons aligned with dashboard_cards order from API */
const DASHBOARD_CARD_ICONS = [
  "users-fill",
  "file-docs",
  "check-circle-cut",
  "alert-circle-fill",
  "cross-circle",
  "chat-fill",
];

const buildFallbackInsights = (payload) => {
  if (!payload?.pieChart?.datasets?.[0]?.data) return null;
  const [pending, approved, rejected] = payload.pieChart.datasets[0].data.map((n) => Number(n) || 0);
  const tq = pending + approved + rejected;
  const reqPerUser = payload?.requests?.user?.req_per_user ?? 0;
  return {
    approval_rate_pct: tq ? Math.round((approved / tq) * 1000) / 10 : 0,
    pending_rate_pct: tq ? Math.round((pending / tq) * 1000) / 10 : 0,
    rejection_rate_pct: tq ? Math.round((rejected / tq) * 1000) / 10 : 0,
    avg_user_messages: reqPerUser,
    new_users_this_month: null,
    new_users_this_week: null,
    active_users_mom_pct: null,
    active_users_wow_pct: null,
  };
};

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const logout = useLogout();

  useEffect(() => {
    setIsLoading(true);
    getDashboardData()
      .then((res) => {
        const status = res.data.status;
        if (status === true) {
          setData(res.data.data);
        } else if (status === false) {
          toast.warning(res.data.message);
          setData(null);
        } else if (status === "expired") {
          toast.error(res.data.message);
          logout();
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load dashboard. Please try again.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [logout]);

  const insights = useMemo(() => data?.insights ?? buildFallbackInsights(data), [data]);

  const meta = data?.meta;
  const cards = data?.cards ?? [];

  const pieEnhanced = useMemo(() => {
    if (!data?.pieChart) return null;
    return {
      ...data.pieChart,
      datasets: data.pieChart.datasets?.map((ds) => ({
        ...ds,
        backgroundColor: ds.backgroundColor ?? ["#f59e0b", "#2563eb", "#64748b"],
        borderWidth: 2,
        borderColor: "#fff",
      })),
    };
  }, [data]);

  return (
    <>
      <Head title="Dashboard" />
      <Content>
        <Block>
          <BlockHead size="sm">
            <BlockBetween>
              <BlockHeadContent>
                <BlockTitle page tag="h2">
                  Operations overview
                </BlockTitle>
                <BlockDes className="text-soft mb-0">
                  User growth, knowledge-base health, and chat volume — aligned with how your UPSLA admin is used day to
                  day.
                </BlockDes>
              </BlockHeadContent>
            </BlockBetween>
          </BlockHead>

          <div className="dashboard-page">
            <div className="dashboard-exec">
              <div className="dashboard-exec__main">
                <span className="dashboard-exec__eyebrow">Analytics</span>
                <h3 className="dashboard-exec__title">Today at a glance</h3>
                <p className="dashboard-exec__scope text-soft mb-0">
                  {meta?.scope ??
                    "Registered users, Q&A moderation pipeline, and end-user messages to the assistant."}
                </p>
              </div>
              {!isLoading && meta?.generated_at ? (
                <div className="dashboard-exec__stamp text-soft">
                  <Icon name="calendar-alt" className="me-1" />
                  <span>
                    Snapshot (IST):{" "}
                    <strong className="text-base">{formatDateTimeIST(meta.generated_at)}</strong>
                  </span>
                  {meta?.calendar_year ? (
                    <span className="d-none d-md-inline ms-md-3">· FY charts use {meta.calendar_year}</span>
                  ) : null}
                </div>
              ) : null}
            </div>

            <Row className="g-gs mb-3">
              {isLoading
                ? Array(6)
                    .fill(0)
                    .map((_, index) => (
                      <Col xl="2" lg="4" md="6" key={index}>
                        <div className="dashboard-stat-skeleton" aria-hidden />
                      </Col>
                    ))
                : cards.map((item, index) => (
                    <Col xl="2" lg="4" md="6" key={`${item.title}-${index}`}>
                      <Link
                        to={item?.slug || "#"}
                        className="d-block text-reset dashboard-stat-card-link"
                        title={item?.hint || item?.title}
                      >
                        <div
                          className="card dashboard-stat-card h-100 mb-0"
                          style={{ backgroundColor: item?.color || "#526484" }}
                        >
                          <div className="card-body dashboard-stat-card__body py-3 px-3">
                            <div className="dashboard-stat-card__top">
                              <div className="dashboard-stat-card__icon" aria-hidden>
                                <Icon name={DASHBOARD_CARD_ICONS[index % DASHBOARD_CARD_ICONS.length]} />
                              </div>
                              <Icon name="chevron-right" className="dashboard-stat-card__chev text-white" />
                            </div>
                            <div className="dashboard-stat-card__metrics">
                              <div className="dashboard-stat-card__label text-white mb-1">{item.title}</div>
                              <div className="dashboard-stat-card__value text-white">{item.count}</div>
                              {item.hint ? (
                                <div className="dashboard-stat-card__hint text-white mt-1">{item.hint}</div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </Link>
                    </Col>
                  ))}
            </Row>

            {!isLoading && insights ? (
              <Row className="g-gs dashboard-insights mb-4">
                <Col md="6" xl="2">
                  <div className="dashboard-insight-tile">
                    <div className="dashboard-insight-tile__label">Approval rate</div>
                    <div className="dashboard-insight-tile__value">{insights.approval_rate_pct}%</div>
                    <p className="dashboard-insight-tile__hint text-soft mb-0">Approved ÷ all Q&amp;A rows</p>
                  </div>
                </Col>
                <Col md="6" xl="2">
                  <div className="dashboard-insight-tile">
                    <div className="dashboard-insight-tile__label">Pending backlog</div>
                    <div className="dashboard-insight-tile__value">{insights.pending_rate_pct}%</div>
                    <p className="dashboard-insight-tile__hint text-soft mb-0">Awaiting moderation</p>
                  </div>
                </Col>
                <Col md="6" xl="2">
                  <div className="dashboard-insight-tile">
                    <div className="dashboard-insight-tile__label">Messages / user</div>
                    <div className="dashboard-insight-tile__value">{insights.avg_user_messages}</div>
                    <p className="dashboard-insight-tile__hint text-soft mb-0">Lifetime bot messages ÷ users</p>
                  </div>
                </Col>
                <Col md="6" xl="2">
                  <div className="dashboard-insight-tile">
                    <div className="dashboard-insight-tile__label">Rejection rate</div>
                    <div className="dashboard-insight-tile__value">{insights.rejection_rate_pct}%</div>
                    <p className="dashboard-insight-tile__hint text-soft mb-0">Rejected ÷ catalogue total</p>
                  </div>
                </Col>
                <Col md="6" xl="2">
                  <div className="dashboard-insight-tile dashboard-insight-tile--accent">
                    <div className="dashboard-insight-tile__label">New users (MTD)</div>
                    <div className="dashboard-insight-tile__value">
                      {insights.new_users_this_month ?? "—"}
                    </div>
                    <p className="dashboard-insight-tile__hint text-soft mb-0">
                      MoM active sign-ups:{" "}
                      {insights.active_users_mom_pct != null ? `${insights.active_users_mom_pct}%` : "—"}
                    </p>
                  </div>
                </Col>
                <Col md="6" xl="2">
                  <div className="dashboard-insight-tile dashboard-insight-tile--accent">
                    <div className="dashboard-insight-tile__label">New users (week)</div>
                    <div className="dashboard-insight-tile__value">{insights.new_users_this_week ?? "—"}</div>
                    <p className="dashboard-insight-tile__hint text-soft mb-0">
                      WoW: {insights.active_users_wow_pct != null ? `${insights.active_users_wow_pct}%` : "—"}
                    </p>
                  </div>
                </Col>
              </Row>
            ) : null}

            <div className="dashboard-section-label mb-2">
              <Icon name="activity-round" />
              <span>Engagement &amp; catalogue</span>
            </div>
            <Row className="g-gs mb-4">
              <Col xxl="6">
                <Row className="g-gs">
                  <Col lg="6" xxl="12">
                    <PreviewAltCard className="dashboard-analytic-card h-100">
                      {isLoading ? (
                        <MyLoader />
                      ) : (
                        <SaleRevenue
                          title="User acquisition"
                          caption="Momentum vs prior periods (active accounts)"
                          data={data?.activities?.activity}
                          chart={data?.activities?.activity_chart}
                        />
                      )}
                    </PreviewAltCard>
                  </Col>
                  <Col lg="6" xxl="12">
                    <Row className="g-gs">
                      <Col sm="6" lg="12" xxl="6">
                        <PreviewAltCard className="dashboard-analytic-card h-100">
                          {isLoading ? (
                            <MyLoader />
                          ) : (
                            <ActiveSubscription
                              title="Active users (YTD)"
                              tooltipText="Count of status=1 users registered in each month of the current year"
                              data={data?.active?.activity}
                              chart={data?.active?.activeSubscription}
                            />
                          )}
                        </PreviewAltCard>
                      </Col>
                      <Col sm="6" lg="12" xxl="6">
                        <PreviewAltCard className="dashboard-analytic-card h-100">
                          {isLoading ? (
                            <MyLoader />
                          ) : (
                            <AvgSubscription
                              title="Rolling 6 months"
                              tooltipText="New registrations by month — trailing window"
                              data={data?.avg?.activity}
                              chart={data?.avg?.activeSubscription}
                            />
                          )}
                        </PreviewAltCard>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
              <Col xxl="6">
                <PreviewAltCard className="dashboard-analytic-card h-100">
                  {isLoading ? (
                    <MyLoader />
                  ) : (
                    <SalesOverview
                      title="Chat traffic"
                      periodLabel="User messages recorded this month (one point per calendar day)."
                      showDownloadMenu={false}
                      data={data?.requests?.user}
                      chart={data?.requests?.salesOverview}
                    />
                  )}
                </PreviewAltCard>
              </Col>
            </Row>

            <div className="dashboard-section-label mb-2">
              <Icon name="grid-alt" />
              <span>Distribution &amp; latest sign-ups</span>
            </div>
            <Row className="g-gs">
              <Col lg="6" xxl="6">
                <PreviewAltCard className="dashboard-chart-card dashboard-analytic-card h-100">
                  {isLoading ? (
                    <MyLoader />
                  ) : (
                    <>
                      <div className="card-title-group align-start gx-3 mb-2">
                        <div className="card-title">
                          <h6 className="title">Q&amp;A moderation mix</h6>
                          <p className="text-soft small mb-0">Pending vs approved vs rejected in the knowledge base</p>
                        </div>
                      </div>
                      <div className="nk-sales-ck large pt-2">
                        <PieChartExample data={pieEnhanced || data?.pieChart} />
                      </div>
                    </>
                  )}
                </PreviewAltCard>
              </Col>
              <Col lg="6" xxl="6">
                <Card className="card-full h-100 dashboard-analytic-card border-0">
                  {isLoading ? (
                    <MyLoader />
                  ) : (
                    <NewsUsers
                      data={data?.today_users || []}
                      heading="Latest registrations"
                      viewAllTo="/user"
                    />
                  )}
                </Card>
              </Col>
            </Row>
          </div>
        </Block>
      </Content>
    </>
  );
};

export default Dashboard;
